import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Search, RefreshCw, Loader2, ShieldCheck, Download } from "lucide-react";

const API = "/api";

const STATUS_LABEL = {
  pending_payment: { label: "Pago pendiente", cls: "bg-amber-500/20 text-amber-300" },
  active: { label: "Activa", cls: "bg-[#1B84FF]/20 text-emerald-300" },
  standby: { label: "Standby", cls: "bg-slate-500/20 text-slate-300" },
  expired: { label: "Vencida", cls: "bg-red-500/20 text-red-300" },
  disabled: { label: "Inhabilitada", cls: "bg-red-500/20 text-red-300" },
};

const expiryColor = (row) => {
  if (!row.expires_at) return "text-slate-400";
  if (row.status === "expired" || row.in_grace || row.days_remaining === 0) return "text-red-400";
  if (row.days_remaining < 30) return "text-amber-300";
  return "text-emerald-300";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return format(new Date(iso), "d MMM yyyy", { locale: es }); } catch (_) { return iso; }
};

const ActionBtn = ({ label, onClick, tone = "slate", testid }) => {
  const tones = {
    green: "bg-[#1B84FF]/15 text-emerald-300 hover:bg-[#1B84FF]/30 border-emerald-500/30",
    red: "bg-red-500/15 text-red-300 hover:bg-red-500/30 border-red-500/30",
    amber: "bg-amber-500/15 text-amber-300 hover:bg-amber-500/30 border-amber-500/30",
    slate: "bg-white/5 text-slate-300 hover:bg-white/15 border-white/10",
    gold: "bg-[#D4A017]/15 text-[#D4A017] hover:bg-[#D4A017]/30 border-[#D4A017]/30",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[11px] px-2 py-1 rounded border transition-colors ${tones[tone]}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
};

export default function AdminSubscriptions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API}/admin/subscriptions`, { params });
      setRows(r.data.subscriptions || []);
    } catch (e) {
      toast.error("Error al cargar suscripciones");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchRows(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewVoucher = async (requestId) => {
    try {
      const r = await axios.get(`${API}/admin/subscription-voucher/${requestId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (_e) {
      toast.error("No se pudo abrir el voucher");
    }
  };

  const doAction = async (row, action, extra = {}) => {
    setBusy((b) => ({ ...b, [row.user_id]: true }));
    try {
      await axios.post(`${API}/admin/subscriptions/${row.user_id}/action`, { action, ...extra });
      toast.success(`Acción "${action}" aplicada a ${row.email}`);
      await fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aplicar acción");
    } finally {
      setBusy((b) => ({ ...b, [row.user_id]: false }));
    }
  };

  const handleExtend = (row) => {
    const v = window.prompt("¿Cuántos meses extender?", "6");
    if (!v) return;
    const months = parseInt(v, 10);
    if (!months || months < 1 || months > 24) { toast.error("Meses inválidos (1-24)"); return; }
    doAction(row, "extend", { months });
  };

  return (
    <div className="min-h-screen bg-[#071B2F] p-6" data-testid="admin-subscriptions-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                <ShieldCheck className="w-6 h-6 text-[#5BC0FF]" /> Suscripciones
              </h1>
              <p className="text-slate-400 text-sm">Aprueba, extiende o pausa membresías. Cada acción queda auditada.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={async () => {
              try {
                const params = {};
                if (search) params.search = search;
                if (statusFilter) params.status = statusFilter;
                const r = await axios.get(`${API}/admin/subscriptions/export`, { params, responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = "avisocar_suscripciones.csv"; a.click();
                URL.revokeObjectURL(url);
                toast.success("CSV exportado (auditado)");
              } catch (_e) { toast.error("Error al exportar CSV"); }
            }} className="border-white/15 text-slate-300" data-testid="subs-export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button variant="outline" onClick={fetchRows} className="border-white/15 text-slate-300" data-testid="subs-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
            </Button>
          </div>
        </div>

        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchRows()}
              placeholder="Buscar por nombre o email..."
              className="pl-9 bg-[#0B2545] border-white/15 text-white"
              data-testid="subs-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#0B2545] border border-white/15 text-slate-300 text-sm"
            data-testid="subs-status-filter"
          >
            <option value="">Todos los estados</option>
            <option value="pending_payment">Pago pendiente</option>
            <option value="active">Activa</option>
            <option value="standby">Standby</option>
            <option value="expired">Vencida</option>
            <option value="disabled">Inhabilitada</option>
          </select>
        </div>

        <div className="bg-[#0B2545] rounded-xl border border-white/10 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="subs-empty">Sin resultados</div>
          ) : (
            <table className="w-full text-sm" data-testid="subs-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-white/10">
                  <th className="p-3">Usuario</th>
                  <th className="p-3">País</th>
                  <th className="p-3">Monto</th>
                  <th className="p-3">Método</th>
                  <th className="p-3">Compra</th>
                  <th className="p-3">Expira</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = STATUS_LABEL[r.status] || { label: r.status, cls: "bg-slate-500/20 text-slate-300" };
                  return (
                    <tr key={r.user_id} className="border-b border-white/10/60 hover:bg-white/10/30" data-testid={`sub-row-${r.user_id}`}>
                      <td className="p-3">
                        <div className="text-white font-medium">{r.full_name}</div>
                        <div className="text-slate-500 text-xs">{r.email}</div>
                      </td>
                      <td className="p-3 text-slate-300">{r.country_code || "PE"}</td>
                      <td className="p-3 text-slate-300">{r.amount != null ? `${r.currency} ${r.amount}` : "—"}</td>
                      <td className="p-3 text-slate-300">{r.method || "—"}</td>
                      <td className="p-3 text-slate-400">{fmtDate(r.purchased_at)}</td>
                      <td className={`p-3 font-medium ${expiryColor(r)}`} data-testid={`sub-expiry-${r.user_id}`}>
                        {fmtDate(r.expires_at)}
                        {r.expires_at && <div className="text-[10px] opacity-70">{r.days_remaining} días{r.in_grace ? " · gracia" : ""}</div>}
                      </td>
                      <td className="p-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`sub-status-${r.user_id}`}>{st.label}</span>
                        {r.has_pending_request && (
                          <div className="text-[10px] text-amber-400 mt-1" data-testid={`sub-pending-req-${r.user_id}`}>Solicitud manual</div>
                        )}
                        {r.pending_voucher && (
                          <button
                            onClick={() => viewVoucher(r.pending_request_id)}
                            className="text-[10px] text-[#5BC0FF] underline mt-0.5 block"
                            data-testid={`sub-voucher-${r.user_id}`}
                          >
                            Ver voucher
                          </button>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {busy[r.user_id] ? (
                            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                          ) : (
                            <>
                              <ActionBtn label="Aprobar" tone="green" onClick={() => doAction(r, "approve")} testid={`sub-approve-${r.user_id}`} />
                              <ActionBtn label="Rechazar" tone="red" onClick={() => doAction(r, "reject")} testid={`sub-reject-${r.user_id}`} />
                              <ActionBtn label="Standby" tone="amber" onClick={() => doAction(r, "standby")} testid={`sub-standby-${r.user_id}`} />
                              <ActionBtn label="Habilitar" tone="slate" onClick={() => doAction(r, "enable")} testid={`sub-enable-${r.user_id}`} />
                              <ActionBtn label="Inhabilitar" tone="red" onClick={() => doAction(r, "disable")} testid={`sub-disable-${r.user_id}`} />
                              <ActionBtn label="Extender" tone="gold" onClick={() => handleExtend(r)} testid={`sub-extend-${r.user_id}`} />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
